import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { map, reduce, isDate, orderBy, isEmpty } from 'lodash'
import moment from 'moment-timezone'

import Loader from 'components/Loader'
import MobileFiltersModal from 'components/Orders/Filters/MobileFiltersModal'
import DesktopFilters from 'components/Orders/Filters/DesktopFilters'

import {
  getIsAllOrdersLoading,
  getOrdersApiParamsConfig,
} from 'store/Orders/selector'
import {
  setAllOrdersFilters,
  fetchAllOrders,
  setOrdersApiParamsConfig,
} from 'store/Orders/actions'
import { getRevenueCenters } from 'store/RevenueCenters/selectors'
import { allOrderStatusesAvailableOptions, orderStates } from 'utils/resources'

const ListFilters = () => {
  const ordersApiParamsConfig = useSelector(getOrdersApiParamsConfig)

  const [startDate, setStartDate] = useState(
    new Date(ordersApiParamsConfig?.fromDate) ?? new Date()
  )
  const [endDate, setEndDate] = useState(
    new Date(ordersApiParamsConfig?.toDate) ?? new Date()
  )
  const [isMobileFiltersModal, showMobileFiltersModal] = useState(false)

  const dispatch = useDispatch()

  const revenueCenters = useSelector(getRevenueCenters)
  const isLoading = useSelector(getIsAllOrdersLoading)

  const transformedRevenueCenters = map(revenueCenters, ({ name, uuid }) => ({
    name,
    uuid,
  }))

  const alphabeticalSortedRevenueCenters = orderBy(
    transformedRevenueCenters,
    ['name'],
    ['asc']
  )

  const onChange = (dates) => {
    const [start, end] = dates

    const fromDate = moment(start).startOf('day').tz('UTC').format()
    const toDate = moment(end).endOf('day').tz('UTC').format()

    if (isDate(start) && isDate(end)) {
      const updatedParams = {
        ...ordersApiParamsConfig,
        fromDate,
        toDate,
        page: 1,
      }

      // update ordersApiParamsConfig
      dispatch(setOrdersApiParamsConfig(updatedParams))

      // fetch orders with new params
      dispatch(fetchAllOrders(updatedParams))
    }

    setStartDate(start)
    setEndDate(end)
  }

  const handleSearchAndSelect = (value) => {
    const { searchQuery, selectedSearchOptionUuid } = value

    // when search query are empty string we have to set them value as null in order to send
    // appropriate value to api
    const searchQueryToSend = isEmpty(searchQuery) ? null : searchQuery

    // updated params set userName, uuid and orderNumber to null and then overwrite
    // one of them in order to remove the previously set value. Value null is required for API value params
    const updatedParams = {
      ...ordersApiParamsConfig,
      page: 1,
      userName: null,
      uuid: null,
      orderNumber: null,
      lastFour: null,
      [selectedSearchOptionUuid]: searchQueryToSend,
    }

    // update ordersApiParamsConfig
    dispatch(setOrdersApiParamsConfig(updatedParams))

    // fetch orders with new params
    dispatch(fetchAllOrders(updatedParams))

    // set searchBarWithSelect values to global store in order to track defaultValues
    dispatch(
      setAllOrdersFilters({
        searchBarWithSelect: {
          ...value,
        },
      })
    )
  }

  const handleSetFilter = (filter) => (value) => {
    const valueToSend = isEmpty(value) ? null : value

    const updatedParams = {
      ...ordersApiParamsConfig,
      page: 1,
      [filter]: valueToSend,
    }

    // update ordersApiParamsConfig
    dispatch(setOrdersApiParamsConfig(updatedParams))

    // fetch orders with new params
    dispatch(fetchAllOrders(updatedParams))
  }

  const onClickFilterBtn = () => {
    showMobileFiltersModal(true)
  }

  const onCloseModal = () => {
    showMobileFiltersModal(false)
  }

  const statesOptions = reduce(
    orderStates,
    (result, value, key) => {
      if (allOrderStatusesAvailableOptions.includes(value)) {
        const option = { uuid: key, name: value }
        return [...result, option]
      }
      return result
    },
    []
  )

  if (isMobileFiltersModal) {
    return isLoading ? (
      <Loader />
    ) : (
      <MobileFiltersModal
        onCloseModal={onCloseModal}
        onChange={onChange}
        startDate={startDate}
        endDate={endDate}
        handleSetFilter={handleSetFilter}
        statesOptions={statesOptions}
        alphabeticalSortedRevenueCenters={alphabeticalSortedRevenueCenters}
      />
    )
  } else {
    return (
      <DesktopFilters
        onChange={onChange}
        startDate={startDate}
        endDate={endDate}
        handleSetFilter={handleSetFilter}
        statesOptions={statesOptions}
        alphabeticalSortedRevenueCenters={alphabeticalSortedRevenueCenters}
        handleSearchAndSelect={handleSearchAndSelect}
        onClickFilterBtn={onClickFilterBtn}
      />
    )
  }
}

export default ListFilters
